import { LeaseApplicationListItem } from 'api/services/applicationService'
import { IdentityVerificationPayload } from 'api/services/verificationService'
import { formatListingPrice } from 'components/propertyCard/formatListingPrice'
import { Unit } from 'newTypes/unit'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { ApplicationPipelineStatus } from 'types/workflowStatus'

import { applicationStatusKey } from './mySpaceUtils'

type SeekerDashboardProps = {
  firstName: string
  applications: LeaseApplicationListItem[] | undefined
  isLoadingApplications: boolean
  listingById: Record<string, Unit | undefined>
  identityVerification: IdentityVerificationPayload | undefined
  profile: {
    name?: string
    email?: string
    phone?: string
  }
  onContinueVerification: () => void
  onCompleteProfile: () => void
  favoritesSlot?: ReactNode
  alertsSlot?: ReactNode
}

function relativeUpdateLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  iso?: string | null
): string {
  if (!iso) return t('account.mySpace.seekerDash.updatedUnknown')
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return t('account.mySpace.seekerDash.updatedUnknown')
  const diffMs = Date.now() - then
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return t('account.mySpace.seekerDash.updatedJustNow')
  if (mins < 60) return t('account.mySpace.seekerDash.updatedMinutes', { count: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 48) return t('account.mySpace.seekerDash.updatedHours', { count: hours })
  const days = Math.floor(hours / 24)
  return t('account.mySpace.seekerDash.updatedDays', { count: days })
}

function seekerStatusTone(status: ApplicationPipelineStatus): string {
  if (status === 'approved') {
    return 'border-emerald-300 bg-emerald-50 text-emerald-800'
  }
  if (status === 'rejected' || status === 'withdrawn') {
    return 'border-gray-300 bg-gray-100 text-gray-600'
  }
  if (
    status === 'identity_required' ||
    status === 'correction_requested' ||
    status === 'documents_requested'
  ) {
    return 'border-amber-300 bg-amber-50 text-amber-900'
  }
  if (status === 'documents_in_review' || status === 'owner_review') {
    return 'border-orange-200 bg-orange-50 text-orange-900'
  }
  return 'border-slate-200 bg-slate-100 text-slate-600'
}

type ChecklistItem = {
  id: string
  label: string
  done: boolean
  optional?: boolean
  inProgress?: boolean
}

const SeekerDashboard = ({
  firstName,
  applications,
  isLoadingApplications,
  listingById,
  identityVerification,
  profile,
  onContinueVerification,
  onCompleteProfile,
  favoritesSlot,
  alertsSlot,
}: SeekerDashboardProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const items = applications ?? []
  const identityStatus = identityVerification?.status ?? 'profile_incomplete'
  const hasDocs = (identityVerification?.documents?.length ?? 0) > 0

  const checklist: ChecklistItem[] = useMemo(() => {
    const hasName = Boolean(profile.name?.trim())
    const hasEmail = Boolean(profile.email?.trim())
    const hasPhone = Boolean(profile.phone?.trim())
    const identityDone = identityStatus === 'identity_approved'
    const identityInProgress =
      identityStatus === 'identity_in_review' ||
      identityStatus === 'identity_pending' ||
      identityStatus === 'identity_correction_requested'

    return [
      {
        id: 'personal',
        label: t('account.mySpace.seekerDash.checkPersonal'),
        done: hasName,
      },
      {
        id: 'email',
        label: t('account.mySpace.seekerDash.checkEmail'),
        done: hasEmail,
      },
      {
        id: 'phone',
        label: t('account.mySpace.seekerDash.checkPhone'),
        done: hasPhone,
      },
      {
        id: 'documents',
        label: t('account.mySpace.seekerDash.checkDocuments'),
        done: hasDocs || identityDone,
        inProgress: hasDocs && !identityDone && identityInProgress,
      },
      {
        id: 'identity',
        label: t('account.mySpace.seekerDash.checkIdentity'),
        done: identityDone,
        inProgress: identityInProgress && !identityDone,
      },
      {
        id: 'income',
        label: t('account.mySpace.seekerDash.checkIncome'),
        done: false,
        optional: true,
      },
    ]
  }, [profile, identityStatus, hasDocs, t])

  const required = checklist.filter(item => !item.optional)
  const completedRequired = required.filter(item => item.done).length
  const progressPct = Math.round((completedRequired / Math.max(required.length, 1)) * 100)

  const actionRequired = useMemo(() => {
    const needingIdentity = items.find(app => {
      const status = applicationStatusKey(app.application_status)
      return (
        status === 'identity_required' ||
        status === 'correction_requested' ||
        status === 'documents_requested'
      )
    })
    if (needingIdentity || identityStatus === 'identity_correction_requested') {
      const listing = needingIdentity?.property_fk
        ? listingById[String(needingIdentity.property_fk)]
        : undefined
      const title =
        needingIdentity?.property_title ||
        listing?.title ||
        t('account.mySpace.untitledListing')
      return {
        title: t('account.mySpace.seekerDash.actionIdentityTitle'),
        body: t('account.mySpace.seekerDash.actionIdentityBody', { title }),
        primaryLabel: t('account.mySpace.seekerDash.continueDossier'),
        onPrimary: onContinueVerification,
        secondaryLabel: t('account.mySpace.browseListings'),
        onSecondary: () => navigate('/listings'),
        detail: title,
      }
    }
    if (identityStatus === 'profile_incomplete' || identityStatus === 'identity_pending') {
      return {
        title: t('account.mySpace.seekerDash.actionCompleteTitle'),
        body: t('account.mySpace.seekerDash.actionCompleteBody'),
        primaryLabel: t('account.mySpace.seekerDash.completeDossier'),
        onPrimary: onContinueVerification,
        secondaryLabel: t('account.mySpace.browseListings'),
        onSecondary: () => navigate('/listings'),
        detail: null as string | null,
      }
    }
    if (items.length === 0) {
      return {
        title: t('account.mySpace.seekerDash.actionSearchTitle'),
        body: t('account.mySpace.seekerDash.actionSearchBody'),
        primaryLabel: t('account.mySpace.browseListings'),
        onPrimary: () => navigate('/listings'),
        secondaryLabel: undefined as string | undefined,
        onSecondary: undefined as (() => void) | undefined,
        detail: null as string | null,
      }
    }
    return null
  }, [
    items,
    identityStatus,
    listingById,
    navigate,
    onContinueVerification,
    t,
  ])

  const greetingName =
    firstName.trim() || t('account.mySpace.seekerDash.fallbackName')

  const visibleApps = items.slice(0, 5)

  return (
    <div className="flex flex-col gap-6">
      <header className="min-w-0">
        <h1 className="break-words text-2xl font-bold text-imovSecondary md:text-3xl">
          {t('account.mySpace.seekerDash.greeting', { name: greetingName })}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 md:text-base">
          {t('account.mySpace.seekerDash.subtitle')}
        </p>
      </header>

      {actionRequired ? (
        <div className="overflow-hidden rounded-2xl border border-amber-200/70 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-l-4 border-amber-400 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                  {t('account.mySpace.seekerDash.actionRequired')}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {t('account.mySpace.seekerDash.nextStep')}
                </span>
              </div>
              <p className="mt-2 font-semibold text-imovSecondary [overflow-wrap:anywhere]">
                {actionRequired.title}
              </p>
              <p className="mt-1 text-sm text-gray-600 [overflow-wrap:anywhere]">
                {actionRequired.body}
              </p>
              {actionRequired.detail ? (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                  <svg
                    className="h-3.5 w-3.5 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {actionRequired.detail}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {actionRequired.onSecondary ? (
                <button
                  type="button"
                  onClick={actionRequired.onSecondary}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50"
                >
                  {actionRequired.secondaryLabel}
                </button>
              ) : null}
              <button
                type="button"
                onClick={actionRequired.onPrimary}
                className="rounded-full bg-imovPrimary px-4 py-2 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
              >
                {actionRequired.primaryLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
        <section id="my-space-applications" className="min-w-0 scroll-mt-24">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-imovSecondary">
              {t('account.mySpace.seekerDash.requestsTitle')}
            </h2>
            {items.length > 0 ? (
              <span className="text-sm font-semibold text-imovPrimary">
                {t('account.mySpace.seekerDash.viewAll', { count: items.length })}
              </span>
            ) : null}
          </div>

          {isLoadingApplications ? (
            <div className="space-y-3">
              {[0, 1].map(i => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-2xl border border-gray-100 bg-white"
                />
              ))}
            </div>
          ) : visibleApps.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
              <p className="text-sm text-gray-600">{t('account.mySpace.noApplications')}</p>
              <Link
                to="/listings"
                className="mt-4 inline-flex rounded-full bg-imovPrimary px-5 py-2.5 text-sm font-semibold text-white hover:bg-imovPrimaryDark"
              >
                {t('account.mySpace.browseListings')}
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {visibleApps.map(app => {
                const status = applicationStatusKey(app.application_status)
                const listing = app.property_fk
                  ? listingById[String(app.property_fk)]
                  : undefined
                const thumb = listing?.images?.[0]
                const title =
                  app.property_title ||
                  listing?.title ||
                  t('account.mySpace.untitledListing')
                const price = listing
                  ? formatListingPrice(listing.price, listing.currency)
                  : null
                const city =
                  listing?.address?.neighborhood ||
                  listing?.address?.city ||
                  ''
                const availability = listing?.availability
                  ? t('account.mySpace.seekerDash.availableFrom', {
                      date: listing.availability,
                    })
                  : null

                return (
                  <li key={String(app.id)}>
                    <article className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md">
                      <button
                        type="button"
                        onClick={() => {
                          if (app.property_fk) {
                            navigate(`/listing/${app.property_fk}`, {
                              state: { returnTo: '/account' },
                            })
                          }
                        }}
                        className="flex w-full flex-col text-left sm:flex-row"
                      >
                        <div className="relative h-36 w-full shrink-0 overflow-hidden bg-violet-50 sm:h-auto sm:w-36 md:w-40">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt=""
                              className="h-full w-full object-cover sm:absolute sm:inset-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full min-h-[8rem] items-center justify-center text-violet-300">
                              <svg
                                className="h-10 w-10"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.5}
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
                                />
                              </svg>
                            </div>
                          )}
                          {price ? (
                            <span className="absolute left-2 top-2 rounded-md bg-white/95 px-2 py-1 text-[11px] font-bold text-imovSecondary shadow-sm">
                              {price}
                            </span>
                          ) : null}
                        </div>

                        <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="truncate text-base font-bold text-imovSecondary">
                                {title}
                              </h3>
                              {city ? (
                                <p className="mt-1 flex items-center gap-1 truncate text-sm text-gray-500">
                                  <svg
                                    className="h-3.5 w-3.5 shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    aria-hidden
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                  </svg>
                                  {city}
                                </p>
                              ) : null}
                              {availability ? (
                                <p className="mt-1 text-xs text-gray-500">{availability}</p>
                              ) : null}
                            </div>
                            <span
                              className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${seekerStatusTone(status)}`}
                            >
                              {t(`account.mySpace.applicationStatus.${status}`)}
                            </span>
                          </div>

                          <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
                            <p className="text-xs text-gray-400">
                              {relativeUpdateLabel(t, app.updated_at || app.created_at)}
                            </p>
                            <span className="text-sm font-semibold text-imovPrimary">
                              {t('account.mySpace.seekerDash.viewTracking')} →
                            </span>
                          </div>
                        </div>
                      </button>
                    </article>
                  </li>
                )
              })}
            </ul>
          )}

          {favoritesSlot ? <div className="mt-6">{favoritesSlot}</div> : null}
          {alertsSlot ? <div className="mt-6">{alertsSlot}</div> : null}
        </section>

        <aside className="lg:sticky lg:top-24">
          <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <h2 className="text-base font-bold text-imovSecondary">
              {t('account.mySpace.seekerDash.dossierTitle')}
            </h2>
            <p className="mt-3 text-sm font-medium text-imovSecondary">
              {t('account.mySpace.seekerDash.dossierProgress', { pct: progressPct })}
            </p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-imovPrimary transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <ul className="mt-5 space-y-3">
              {checklist.map(item => (
                <li key={item.id} className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    {item.done ? (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-imovPrimary text-white">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    ) : (
                      <span className="h-5 w-5 shrink-0 rounded-full border-2 border-gray-300" />
                    )}
                    <span
                      className={`truncate text-sm ${
                        item.done ? 'text-imovSecondary' : 'text-gray-500'
                      }`}
                    >
                      {item.label}
                    </span>
                  </div>
                  {item.inProgress ? (
                    <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-imovPrimary">
                      {t('account.mySpace.seekerDash.inProgress')}
                    </span>
                  ) : null}
                  {item.optional && !item.done ? (
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                      {t('account.mySpace.seekerDash.optional')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={onCompleteProfile}
              className="mt-5 w-full rounded-full bg-imovPrimary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
            >
              {t('account.mySpace.seekerDash.completeDossier')}
            </button>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default SeekerDashboard
